import { useState, useEffect, useCallback } from 'react';
import { promoCodesApi, PromoCode, CreatePromoCodeDto, UpdatePromoCodeDto } from '../../../api/payments';
import { toast } from 'sonner';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UsePromoCodesReturn, PromoCodeFormErrors } from '../types';
import { getErrorMessage } from '@/lib/errors';

const initialFormData: CreatePromoCodeDto = {
  code: '',
  type: 'percent',
  value: 10,
  max_uses: null,
  valid_from: null,
  valid_until: null,
  is_active: true,
};

export function usePromoCodes(): UsePromoCodesReturn {
  const confirm = useConfirm();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // По умолчанию показываем все промокоды (вкл. неактивные) — иначе после деактивации
  // промокод пропадает из таблицы, и им нельзя управлять.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState<CreatePromoCodeDto>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<PromoCodeFormErrors>({});

  // Валидация формы. Возвращает карту ошибок по полям (пустая = валидно).
  const validateForm = (requireCode: boolean): PromoCodeFormErrors => {
    const next: PromoCodeFormErrors = {};
    if (requireCode && !formData.code.trim()) {
      next.code = 'Укажите код промокода';
    }
    if (formData.type === 'percent' && (formData.value < 1 || formData.value > 100)) {
      next.value = 'Процент должен быть от 1 до 100';
    } else if (formData.type === 'fixed' && formData.value < 1) {
      next.value = 'Сумма должна быть больше 0';
    }
    if (formData.valid_from && formData.valid_until && new Date(formData.valid_from) > new Date(formData.valid_until)) {
      next.valid_until = 'Дата начала не может быть позже даты окончания';
    }
    return next;
  };

  const clearFieldError = (field: keyof PromoCodeFormErrors) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const fetchPromoCodes = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await promoCodesApi.getAll(includeInactive);
      setPromoCodes(data);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить промокоды'));
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const handleCreate = async () => {
    if (isSubmitting) return;
    const validationErrors = validateForm(true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const payload: CreatePromoCodeDto = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        value: formData.value,
        max_uses: formData.max_uses || null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        is_active: formData.is_active,
      };

      setIsSubmitting(true);
      await promoCodesApi.create(payload);
      toast.success('Промокод создан');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось создать промокод'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    setErrors({});
    setFormData({
      code: promoCode.code,
      type: promoCode.type,
      value: promoCode.value,
      max_uses: promoCode.max_uses,
      valid_from: promoCode.valid_from || null,
      valid_until: promoCode.valid_until || null,
      is_active: promoCode.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPromoCode || isSubmitting) return;
    const validationErrors = validateForm(true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const payload: UpdatePromoCodeDto = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        value: formData.value,
        max_uses: formData.max_uses || null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        is_active: formData.is_active,
      };

      setIsSubmitting(true);
      await promoCodesApi.update(editingPromoCode.id, payload);
      toast.success('Промокод обновлён');
      setIsEditDialogOpen(false);
      setEditingPromoCode(null);
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось обновить промокод'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить промокод?',
      description: 'Это действие необратимо.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await promoCodesApi.delete(id);
      toast.success('Промокод удалён');
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить промокод'));
    }
  };

  // Открытие диалога создания сбрасывает прежние ошибки и данные прошлого редактирования.
  const openCreateDialog = (open: boolean) => {
    if (open) {
      setErrors({});
      setFormData(initialFormData);
    }
    setIsCreateDialogOpen(open);
  };

  return {
    loadError,
    retryLoad: fetchPromoCodes,
    promoCodes,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPromoCode,
    formData,
    isSubmitting,
    errors,
    clearFieldError,
    setIncludeInactive,
    setIsCreateDialogOpen: openCreateDialog,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  };
}
